import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";
import { CONFIG } from "@/lib/config";
import { XLogo } from "@/components/bits";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const f = () => setScrolled(window.scrollY > 24);
        f();
        window.addEventListener("scroll", f, { passive: true });
        return () => window.removeEventListener("scroll", f);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="vera-navbar"
            className={`fixed top-0 inset-x-0 z-[80] transition-all duration-300 ${scrolled || open ? "bg-vera-bg/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"}`}
        >
            <nav className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between" aria-label="Main navigation">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0">
                    <img src={CONFIG.LOGO} alt="VERA — Verified Real Assets logo" className="h-10 w-10 object-contain mask-fade-circle" />
                    <span className="hidden sm:block font-black tracking-[0.4em] text-sm text-vera-white">VERA</span>
                </Link>

                <div className="hidden lg:flex items-center gap-7">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.label}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `font-mono text-[11px] tracking-[0.18em] transition-colors duration-200 ${isActive ? "text-vera-gold2" : "text-vera-mut hover:text-white"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href={CONFIG.BUY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-buy-button"
                        className="hidden sm:inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] border border-vera-gold/60 text-vera-gold2 bg-vera-gold/5 hover:bg-vera-gold/15 px-5 py-2.5 transition-all"
                    >
                        BUY <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </a>
                    <a
                        href={CONFIG.X_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="VERA on X"
                        data-testid="nav-x-link"
                        className="hidden sm:flex items-center justify-center w-10 h-10 border border-white/15 text-vera-mut hover:text-white hover:border-vera-soft/60 transition-all"
                    >
                        <XLogo />
                    </a>
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden flex items-center justify-center w-10 h-10 border border-white/15 text-vera-white"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.28 }}
                        className="lg:hidden overflow-hidden bg-vera-bg/97 backdrop-blur-xl border-b border-white/10"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-5 sm:px-8 py-6 flex flex-col">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div key={l.label} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
                                    <Link
                                        to={l.to}
                                        data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                        className="flex items-center justify-between py-4 border-b border-white/5 text-xl font-bold tracking-wide text-vera-white"
                                    >
                                        {l.label}
                                        <span className="font-mono text-[10px] text-vera-mut">{String(i + 1).padStart(2, "0")}</span>
                                    </Link>
                                </motion.div>
                            ))}
                            <div className="flex items-center gap-3 pt-6">
                                <a href={CONFIG.ARGUS_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-buy-button" className="flex-1 inline-flex justify-center items-center gap-2 font-mono text-[11px] tracking-[0.22em] border border-vera-gold/60 text-vera-gold2 bg-vera-gold/5 px-5 py-3.5">
                                    BUY <ArrowUpRight className="w-3.5 h-3.5" />
                                </a>
                                <a href={CONFIG.X_URL} target="_blank" rel="noopener noreferrer" aria-label="VERA on X" data-testid="nav-mobile-x-link" className="flex items-center justify-center w-12 h-12 border border-white/15 text-vera-mut">
                                    <XLogo />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
